/**
 * Functional Matrix Loader (browser-safe)
 *
 * Loads and provides access to the GeoEvent to Velocity component mapping
 * data. This module is intentionally browser-safe: it has zero Node-only
 * imports and operates on an already-parsed matrix JSON object.
 *
 * Node entry points use `loadFunctionalMatrix(path)` from
 * `./matrix-loader-node.ts` to read the file from disk and delegate here.
 * Browser entry points fetch the JSON (or import it via esbuild) and call
 * `buildFunctionalMatrix(raw)` directly.
 */

import type {
  FunctionalMatrix,
  FunctionalMatrixEntry,
  CompatibilityLevel,
  ParameterMapping,
} from '../types/index.js';

/**
 * Raw JSON structure for input/output mappings
 */
interface RawConnectorMapping {
  geoEventConnector: string;
  geoEventConnectorLabel: string;
  geoEventHelpUrl: string;
  velocityEquivalent: string;
  velocityEquivalentEnterprise?: string;
  velocityEquivalentInPlace?: string;
  velocityType: string;
  velocityHelpUrl: string;
  velocityHelpUrlEnterprise?: string;
  velocityHelpUrlInPlace?: string;
  compatibility: string;
  compatibilityEnterprise?: string;
  compatibilityNotes: string;
  compatibilityNotesEnterprise?: string;
  geoEventGalleryItemId?: string;
  geoEventGalleryItemDeprecated?: boolean;
  limitations: string[];
  parameterMappings: Array<{
    geoEventParam: string;
    geoEventParamLabel: string;
    velocityParam: string;
    velocityParamLabel: string;
  }>;
  parameterMappingsEnterprise?: Array<{
    geoEventParam: string;
    geoEventParamLabel: string;
    velocityParam: string;
    velocityParamLabel: string;
  }>;
  alternatives: Array<{
    velocityComponent: string;
    description: string;
    tradeoffs: string[];
    recommended: boolean;
  }>;
  migrationSteps?: Array<{
    step: number;
    action: string;
    manual: boolean;
    autoValue?: string;
  }>;
}

/**
 * Raw JSON structure for processor mappings
 */
interface RawProcessorMapping {
  geoEventProcessor: string;
  geoEventProcessorLabel: string;
  geoEventHelpUrl: string;
  velocityEquivalent: string;
  velocityType: string;
  velocityHelpUrl: string;
  velocityHelpUrlEnterprise?: string;
  compatibility: string;
  compatibilityNotes: string;
  geoEventGalleryItemId?: string;
  geoEventGalleryItemDeprecated?: boolean;
  limitations: string[];
  parameterMappings: Array<{
    geoEventParam: string;
    geoEventParamLabel: string;
    velocityParam: string;
    velocityParamLabel: string;
  }>;
  alternatives: Array<{
    velocityComponent: string;
    description: string;
    tradeoffs: string[];
    recommended: boolean;
  }>;
  migrationSteps?: Array<{
    step: number;
    action: string;
    manual: boolean;
    autoValue?: string;
  }>;
  arcadeAlternative?: { functions: string[]; note: string };
}

/**
 * Raw JSON structure for filter mappings
 */
interface RawFilterMapping {
  geoEventFilter: string;
  geoEventFilterLabel: string;
  geoEventHelpUrl: string;
  velocityEquivalent: string;
  velocityType: string;
  velocityHelpUrl: string;
  velocityHelpUrlEnterprise?: string;
  compatibility: string;
  compatibilityNotes: string;
  geoEventGalleryItemId?: string;
  geoEventGalleryItemDeprecated?: boolean;
  limitations: string[];
  parameterMappings: Array<{
    geoEventParam: string;
    geoEventParamLabel: string;
    velocityParam: string;
    velocityParamLabel: string;
  }>;
  alternatives: Array<{
    velocityComponent: string;
    description: string;
    tradeoffs: string[];
    recommended: boolean;
  }>;
  migrationSteps?: Array<{
    step: number;
    action: string;
    manual: boolean;
    autoValue?: string;
  }>;
}

/**
 * Raw JSON structure for the functional matrix file
 */
interface RawFunctionalMatrix {
  version: string;
  description: string;
  inputs: RawConnectorMapping[];
  outputs: RawConnectorMapping[];
  processors: RawProcessorMapping[];
  filters: RawFilterMapping[];
  adapters: {
    inbound: Array<{
      uri: string;
      label: string;
      description: string;
      velocityEquivalent: string;
      compatibility: string;
    }>;
    outbound: Array<{
      uri: string;
      label: string;
      description: string;
      velocityEquivalent: string;
      compatibility: string;
    }>;
  };
  transports: {
    inbound: Array<{
      uri: string;
      label: string;
      description: string;
      velocityEquivalent: string;
      compatibility: string;
    }>;
    outbound: Array<{
      uri: string;
      label: string;
      description: string;
      velocityEquivalent: string;
      compatibility: string;
    }>;
  };
  parameterLabels: Record<string, string>;
  velocityOnlyComponents?: {
    description: string;
    availability: string;
    tools: Array<Record<string, unknown>>;
    outputs: Array<Record<string, unknown>>;
    sources: Array<Record<string, unknown>>;
    feeds: Array<Record<string, unknown>>;
  };
}

const COMPATIBILITY_LEVELS = new Set(['full', 'partial', 'none']);

type MatrixRecord = Record<string, unknown>;

export class FunctionalMatrixValidationError extends Error {
  public readonly validationErrors: string[];

  constructor(validationErrors: string[]) {
    super(`Functional matrix validation failed: ${validationErrors.join('; ')}`);
    this.name = 'FunctionalMatrixValidationError';
    this.validationErrors = validationErrors;
  }
}

function isRecord(value: unknown): value is MatrixRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function requireRecord(errors: string[], value: unknown, path: string): value is MatrixRecord {
  if (isRecord(value)) return true;
  errors.push(`${path} must be an object`);
  return false;
}

function requireArray(errors: string[], value: unknown, path: string): value is unknown[] {
  if (Array.isArray(value)) return true;
  errors.push(`${path} must be an array`);
  return false;
}

function requireString(errors: string[], obj: MatrixRecord, field: string, path: string): boolean {
  if (typeof obj[field] === 'string') return true;
  errors.push(`${path}.${field} must be a string`);
  return false;
}

function optionalString(errors: string[], obj: MatrixRecord, field: string, path: string): boolean {
  const value = obj[field];
  if (value === undefined || typeof value === 'string') return true;
  errors.push(`${path}.${field} must be a string when present`);
  return false;
}

function optionalBoolean(errors: string[], obj: MatrixRecord, field: string, path: string): boolean {
  const value = obj[field];
  if (value === undefined || typeof value === 'boolean') return true;
  errors.push(`${path}.${field} must be a boolean when present`);
  return false;
}

/**
 * Validates an optional ArcGIS Online item id. AGOL item ids are 32-char
 * lowercase hex strings. Reject anything else early so a typo in the matrix
 * surfaces at load time rather than producing a 404 link in the guide.
 */
function optionalGalleryItemId(errors: string[], obj: MatrixRecord, path: string): boolean {
  const value = obj['geoEventGalleryItemId'];
  if (value === undefined) return true;
  if (typeof value !== 'string') {
    errors.push(`${path}.geoEventGalleryItemId must be a string when present`);
    return false;
  }
  if (!/^[0-9a-f]{32}$/.test(value)) {
    errors.push(`${path}.geoEventGalleryItemId must be a 32-character lowercase hex AGOL item id`);
    return false;
  }
  return true;
}

function requireBoolean(errors: string[], obj: MatrixRecord, field: string, path: string): boolean {
  if (typeof obj[field] === 'boolean') return true;
  errors.push(`${path}.${field} must be a boolean`);
  return false;
}

function validateCompatibility(errors: string[], obj: MatrixRecord, field: string, path: string, optional = false): boolean {
  const value = obj[field];
  if (value === undefined && optional) return true;
  if (typeof value === 'string' && COMPATIBILITY_LEVELS.has(value)) return true;
  errors.push(`${path}.${field} must be one of: full, partial, none`);
  return false;
}

function validateStringArray(errors: string[], value: unknown, path: string): boolean {
  if (!requireArray(errors, value, path)) return false;
  let valid = true;
  value.forEach((item, index) => {
    if (typeof item !== 'string') {
      errors.push(`${path}[${index}] must be a string`);
      valid = false;
    }
  });
  return valid;
}

function validateUniqueStringField(errors: string[], entries: unknown[], field: string, path: string): boolean {
  let valid = true;
  const seen = new Map<string, number>();
  entries.forEach((entry, index) => {
    if (!isRecord(entry) || typeof entry[field] !== 'string') return;
    const value = entry[field];
    const firstIndex = seen.get(value);
    if (firstIndex !== undefined) {
      errors.push(`${path}[${index}].${field} duplicates ${path}[${firstIndex}].${field}: ${value}`);
      valid = false;
      return;
    }
    seen.set(value, index);
  });
  return valid;
}

function validateParameterMappings(errors: string[], value: unknown, path: string): boolean {
  if (!requireArray(errors, value, path)) return false;
  let valid = true;
  value.forEach((mapping, index) => {
    const itemPath = `${path}[${index}]`;
    if (!requireRecord(errors, mapping, itemPath)) {
      valid = false;
      return;
    }
    for (const field of ['geoEventParam', 'geoEventParamLabel', 'velocityParam', 'velocityParamLabel']) {
      valid = requireString(errors, mapping, field, itemPath) && valid;
    }
  });
  return valid;
}

function validateAlternatives(errors: string[], value: unknown, path: string): boolean {
  if (!requireArray(errors, value, path)) return false;
  let valid = true;
  value.forEach((alternative, index) => {
    const itemPath = `${path}[${index}]`;
    if (!requireRecord(errors, alternative, itemPath)) {
      valid = false;
      return;
    }
    for (const field of ['velocityComponent', 'description']) {
      valid = requireString(errors, alternative, field, itemPath) && valid;
    }
    valid = validateStringArray(errors, alternative.tradeoffs, `${itemPath}.tradeoffs`) && valid;
    valid = requireBoolean(errors, alternative, 'recommended', itemPath) && valid;
  });
  return valid;
}

function validateMigrationSteps(errors: string[], value: unknown, path: string): boolean {
  if (value === undefined) return true;
  if (!requireArray(errors, value, path)) return false;
  let valid = true;
  value.forEach((step, index) => {
    const itemPath = `${path}[${index}]`;
    if (!requireRecord(errors, step, itemPath)) {
      valid = false;
      return;
    }
    if (typeof step.step !== 'number') {
      errors.push(`${itemPath}.step must be a number`);
      valid = false;
    }
    valid = requireString(errors, step, 'action', itemPath) && valid;
    valid = requireBoolean(errors, step, 'manual', itemPath) && valid;
    valid = optionalString(errors, step, 'autoValue', itemPath) && valid;
  });
  return valid;
}

function validateConnectorMapping(errors: string[], value: unknown, path: string): boolean {
  if (!requireRecord(errors, value, path)) return false;
  let valid = true;
  for (const field of ['geoEventConnector', 'geoEventConnectorLabel', 'geoEventHelpUrl', 'velocityEquivalent', 'velocityType', 'velocityHelpUrl', 'compatibilityNotes']) {
    valid = requireString(errors, value, field, path) && valid;
  }
  for (const field of ['velocityEquivalentEnterprise', 'velocityEquivalentInPlace', 'velocityHelpUrlEnterprise', 'velocityHelpUrlInPlace', 'compatibilityNotesEnterprise']) {
    valid = optionalString(errors, value, field, path) && valid;
  }
  valid = validateCompatibility(errors, value, 'compatibility', path) && valid;
  valid = validateCompatibility(errors, value, 'compatibilityEnterprise', path, true) && valid;
  valid = optionalGalleryItemId(errors, value, path) && valid;
  valid = optionalBoolean(errors, value, 'geoEventGalleryItemDeprecated', path) && valid;
  valid = validateStringArray(errors, value.limitations, `${path}.limitations`) && valid;
  valid = validateParameterMappings(errors, value.parameterMappings, `${path}.parameterMappings`) && valid;
  if (value.parameterMappingsEnterprise !== undefined) {
    valid = validateParameterMappings(errors, value.parameterMappingsEnterprise, `${path}.parameterMappingsEnterprise`) && valid;
  }
  valid = validateAlternatives(errors, value.alternatives, `${path}.alternatives`) && valid;
  valid = validateMigrationSteps(errors, value.migrationSteps, `${path}.migrationSteps`) && valid;
  return valid;
}

function validateProcessorOrFilterMapping(errors: string[], value: unknown, path: string, prefix: 'Processor' | 'Filter'): boolean {
  if (!requireRecord(errors, value, path)) return false;
  let valid = true;
  const sourceField = prefix === 'Processor' ? 'geoEventProcessor' : 'geoEventFilter';
  const sourceLabelField = prefix === 'Processor' ? 'geoEventProcessorLabel' : 'geoEventFilterLabel';
  for (const field of [sourceField, sourceLabelField, 'geoEventHelpUrl', 'velocityEquivalent', 'velocityType', 'velocityHelpUrl', 'compatibilityNotes']) {
    valid = requireString(errors, value, field, path) && valid;
  }
  valid = validateCompatibility(errors, value, 'compatibility', path) && valid;
  valid = optionalGalleryItemId(errors, value, path) && valid;
  valid = optionalBoolean(errors, value, 'geoEventGalleryItemDeprecated', path) && valid;
  valid = validateStringArray(errors, value.limitations, `${path}.limitations`) && valid;
  valid = validateParameterMappings(errors, value.parameterMappings, `${path}.parameterMappings`) && valid;
  valid = validateAlternatives(errors, value.alternatives, `${path}.alternatives`) && valid;
  valid = validateMigrationSteps(errors, value.migrationSteps, `${path}.migrationSteps`) && valid;
  return valid;
}

function validateAdapterOrTransport(errors: string[], value: unknown, path: string): boolean {
  if (!requireRecord(errors, value, path)) return false;
  let valid = true;
  for (const field of ['uri', 'label', 'description', 'velocityEquivalent']) {
    valid = requireString(errors, value, field, path) && valid;
  }
  valid = validateCompatibility(errors, value, 'compatibility', path) && valid;
  return valid;
}

function validateVelocityOnlyComponents(errors: string[], value: unknown, path: string): boolean {
  if (value === undefined) return true;
  if (!requireRecord(errors, value, path)) return false;
  let valid = true;
  valid = requireString(errors, value, 'description', path) && valid;
  valid = requireString(errors, value, 'availability', path) && valid;
  for (const field of ['tools', 'outputs', 'sources', 'feeds']) {
    if (requireArray(errors, value[field], `${path}.${field}`)) {
      value[field].forEach((component, index) => {
        const itemPath = `${path}.${field}[${index}]`;
        if (!requireRecord(errors, component, itemPath)) {
          valid = false;
          return;
        }
        for (const requiredField of ['velocityEquivalent', 'velocityType', 'velocityHelpUrl', 'description']) {
          valid = requireString(errors, component, requiredField, itemPath) && valid;
        }
      });
    } else {
      valid = false;
    }
  }
  return valid;
}

function assertValidFunctionalMatrix(raw: unknown): asserts raw is RawFunctionalMatrix {
  const errors: string[] = [];
  if (!requireRecord(errors, raw, 'matrix')) {
    throw new FunctionalMatrixValidationError(errors);
  }

  let valid = true;
  valid = requireString(errors, raw, 'version', 'matrix') && valid;
  valid = requireString(errors, raw, 'description', 'matrix') && valid;

  for (const section of ['inputs', 'outputs'] as const) {
    if (requireArray(errors, raw[section], `matrix.${section}`)) {
      raw[section].forEach((entry, index) => {
        valid = validateConnectorMapping(errors, entry, `matrix.${section}[${index}]`) && valid;
      });
      valid = validateUniqueStringField(errors, raw[section], 'geoEventConnector', `matrix.${section}`) && valid;
    } else {
      valid = false;
    }
  }

  if (requireArray(errors, raw.processors, 'matrix.processors')) {
    raw.processors.forEach((entry, index) => {
      valid = validateProcessorOrFilterMapping(errors, entry, `matrix.processors[${index}]`, 'Processor') && valid;
    });
    valid = validateUniqueStringField(errors, raw.processors, 'geoEventProcessor', 'matrix.processors') && valid;
  } else {
    valid = false;
  }

  if (requireArray(errors, raw.filters, 'matrix.filters')) {
    raw.filters.forEach((entry, index) => {
      valid = validateProcessorOrFilterMapping(errors, entry, `matrix.filters[${index}]`, 'Filter') && valid;
    });
    valid = validateUniqueStringField(errors, raw.filters, 'geoEventFilter', 'matrix.filters') && valid;
  } else {
    valid = false;
  }

  for (const section of ['adapters', 'transports'] as const) {
    if (!requireRecord(errors, raw[section], `matrix.${section}`)) {
      valid = false;
      continue;
    }
    for (const direction of ['inbound', 'outbound'] as const) {
      if (requireArray(errors, raw[section][direction], `matrix.${section}.${direction}`)) {
        raw[section][direction].forEach((entry, index) => {
          valid = validateAdapterOrTransport(errors, entry, `matrix.${section}.${direction}[${index}]`) && valid;
        });
        valid = validateUniqueStringField(errors, raw[section][direction], 'uri', `matrix.${section}.${direction}`) && valid;
      } else {
        valid = false;
      }
    }
  }

  if (requireRecord(errors, raw.parameterLabels, 'matrix.parameterLabels')) {
    for (const [key, value] of Object.entries(raw.parameterLabels)) {
      if (typeof value !== 'string') {
        errors.push(`matrix.parameterLabels.${key} must be a string`);
        valid = false;
      }
    }
  } else {
    valid = false;
  }

  valid = validateVelocityOnlyComponents(errors, raw.velocityOnlyComponents, 'matrix.velocityOnlyComponents') && valid;

  if (!valid) {
    throw new FunctionalMatrixValidationError(errors);
  }
}

/**
 * Extended functional matrix with lookup capabilities
 */
export interface LoadedFunctionalMatrix extends FunctionalMatrix {
  inputsByConnector: Map<string, FunctionalMatrixEntry>;
  outputsByConnector: Map<string, FunctionalMatrixEntry>;
  processorsByUri: Map<string, FunctionalMatrixEntry>;
  filtersByType: Map<string, FunctionalMatrixEntry>;
  parameterLabels: Map<string, string>;
  adaptersByUri: Map<string, { label: string; velocityEquivalent: string; compatibility: CompatibilityLevel }>;
  transportsByUri: Map<string, { label: string; velocityEquivalent: string; compatibility: CompatibilityLevel }>;
  velocityOnlyComponents?: import('../types/mapping.js').VelocityOnlyComponent[];
}

/**
 * Converts raw parameter mappings to typed format
 */
function convertParameterMappings(
  raw: Array<{
    geoEventParam: string;
    geoEventParamLabel: string;
    velocityParam: string;
    velocityParamLabel: string;
  }>
): ParameterMapping[] {
  return raw.map((p) => ({
    geoEventParam: p.geoEventParam,
    geoEventParamLabel: p.geoEventParamLabel,
    velocityParam: p.velocityParam,
    velocityParamLabel: p.velocityParamLabel,
  }));
}

/**
 * Converts a raw connector mapping to FunctionalMatrixEntry
 */
function convertConnectorMapping(raw: RawConnectorMapping): FunctionalMatrixEntry {
  return {
    geoEventConnector: raw.geoEventConnector,
    geoEventConnectorLabel: raw.geoEventConnectorLabel,
    geoEventHelpUrl: raw.geoEventHelpUrl,
    velocityEquivalent: raw.velocityEquivalent,
    velocityEquivalentEnterprise: raw.velocityEquivalentEnterprise,
    velocityEquivalentInPlace: raw.velocityEquivalentInPlace,
    velocityHelpUrl: raw.velocityHelpUrl,
    velocityHelpUrlEnterprise: raw.velocityHelpUrlEnterprise,
    velocityHelpUrlInPlace: raw.velocityHelpUrlInPlace,
    compatibility: raw.compatibility as import('../types/mapping.js').CompatibilityLevel || undefined,
    compatibilityEnterprise: raw.compatibilityEnterprise as import('../types/mapping.js').CompatibilityLevel || undefined,
    compatibilityNotes: raw.compatibilityNotes,
    compatibilityNotesEnterprise: raw.compatibilityNotesEnterprise,
    parameterMappings: JSON.stringify(convertParameterMappings(raw.parameterMappings)),
    parameterMappingsEnterprise: raw.parameterMappingsEnterprise
      ? JSON.stringify(convertParameterMappings(raw.parameterMappingsEnterprise))
      : undefined,
    migrationSteps: raw.migrationSteps,
    geoEventGalleryItemId: raw.geoEventGalleryItemId,
    geoEventGalleryItemDeprecated: raw.geoEventGalleryItemDeprecated,
  };
}

/**
 * Converts a raw processor mapping to FunctionalMatrixEntry
 */
function convertProcessorMapping(raw: RawProcessorMapping): FunctionalMatrixEntry {
  return {
    geoEventConnector: raw.geoEventProcessor,
    geoEventConnectorLabel: raw.geoEventProcessorLabel,
    geoEventHelpUrl: raw.geoEventHelpUrl,
    velocityEquivalent: raw.velocityEquivalent,
    velocityType: raw.velocityType,
    velocityHelpUrl: raw.velocityHelpUrl,
    velocityHelpUrlEnterprise: raw.velocityHelpUrlEnterprise,
    compatibility: raw.compatibility as import('../types/mapping.js').CompatibilityLevel || undefined,
    compatibilityNotes: raw.compatibilityNotes,
    parameterMappings: JSON.stringify(convertParameterMappings(raw.parameterMappings)),
    migrationSteps: raw.migrationSteps,
    geoEventGalleryItemId: raw.geoEventGalleryItemId,
    geoEventGalleryItemDeprecated: raw.geoEventGalleryItemDeprecated,
    arcadeAlternative: raw.arcadeAlternative,
  };
}

/**
 * Converts a raw filter mapping to FunctionalMatrixEntry
 */
function convertFilterMapping(raw: RawFilterMapping): FunctionalMatrixEntry {
  return {
    geoEventConnector: raw.geoEventFilter,
    geoEventConnectorLabel: raw.geoEventFilterLabel,
    geoEventHelpUrl: raw.geoEventHelpUrl,
    velocityEquivalent: raw.velocityEquivalent,
    velocityType: raw.velocityType,
    velocityHelpUrl: raw.velocityHelpUrl,
    velocityHelpUrlEnterprise: raw.velocityHelpUrlEnterprise,
    compatibility: raw.compatibility as import('../types/mapping.js').CompatibilityLevel || undefined,
    compatibilityNotes: raw.compatibilityNotes,
    parameterMappings: JSON.stringify(convertParameterMappings(raw.parameterMappings)),
    migrationSteps: raw.migrationSteps,
    geoEventGalleryItemId: raw.geoEventGalleryItemId,
    geoEventGalleryItemDeprecated: raw.geoEventGalleryItemDeprecated,
  };
}

/**
 * Pure, browser-safe builder. Accepts the parsed functional-matrix JSON
 * (as returned by `JSON.parse`) and returns the indexed runtime structure.
 *
 * Throws `FunctionalMatrixValidationError` if the input does not satisfy
 * the structural schema.
 */
export function buildFunctionalMatrix(raw: unknown): LoadedFunctionalMatrix {
  assertValidFunctionalMatrix(raw);

  // Convert to FunctionalMatrixEntry arrays
  const inputs = raw.inputs.map(convertConnectorMapping);
  const outputs = raw.outputs.map(convertConnectorMapping);
  const processors = raw.processors.map(convertProcessorMapping);
  const filters = raw.filters.map(convertFilterMapping);

  // Build lookup maps
  const inputsByConnector = new Map<string, FunctionalMatrixEntry>();
  for (const input of inputs) {
    inputsByConnector.set(input.geoEventConnector, input);
  }

  const outputsByConnector = new Map<string, FunctionalMatrixEntry>();
  for (const output of outputs) {
    outputsByConnector.set(output.geoEventConnector, output);
  }

  const processorsByUri = new Map<string, FunctionalMatrixEntry>();
  for (const processor of processors) {
    processorsByUri.set(processor.geoEventConnector, processor);
    // Also add without version suffix for flexible matching
    const baseUri = processor.geoEventConnector.replace(/\/\d+\.\d+\.\d+$/, '');
    if (baseUri !== processor.geoEventConnector) {
      processorsByUri.set(baseUri, processor);
    }
  }

  const filtersByType = new Map<string, FunctionalMatrixEntry>();
  for (const filter of filters) {
    filtersByType.set(filter.geoEventConnector, filter);
  }

  const parameterLabels = new Map<string, string>();
  for (const [key, value] of Object.entries(raw.parameterLabels)) {
    parameterLabels.set(key, value);
  }

  const adaptersByUri = new Map<string, { label: string; velocityEquivalent: string; compatibility: CompatibilityLevel }>();
  for (const adapter of [...raw.adapters.inbound, ...raw.adapters.outbound]) {
    adaptersByUri.set(adapter.uri, {
      label: adapter.label,
      velocityEquivalent: adapter.velocityEquivalent,
      compatibility: adapter.compatibility as CompatibilityLevel,
    });
    // Also add with version suffix pattern
    const baseUri = adapter.uri;
    adaptersByUri.set(baseUri, {
      label: adapter.label,
      velocityEquivalent: adapter.velocityEquivalent,
      compatibility: adapter.compatibility as CompatibilityLevel,
    });
  }

  const transportsByUri = new Map<string, { label: string; velocityEquivalent: string; compatibility: CompatibilityLevel }>();
  for (const transport of [...raw.transports.inbound, ...raw.transports.outbound]) {
    transportsByUri.set(transport.uri, {
      label: transport.label,
      velocityEquivalent: transport.velocityEquivalent,
      compatibility: transport.compatibility as CompatibilityLevel,
    });
  }

  // Load velocity-only components if present
  const vocRaw = raw.velocityOnlyComponents;
  let velocityOnlyComponents: import('../types/mapping.js').VelocityOnlyComponent[] | undefined;
  if (vocRaw) {
    velocityOnlyComponents = [
      ...vocRaw.tools,
      ...vocRaw.outputs,
      ...vocRaw.sources,
      ...vocRaw.feeds,
    ].map((c: Record<string, unknown>) => ({
      velocityEquivalent: c.velocityEquivalent as string,
      velocityType: c.velocityType as string,
      velocityHelpUrl: c.velocityHelpUrl as string,
      description: c.description as string,
      capabilities: c.capabilities as string[] | undefined,
      keyProperties: c.keyProperties as string[] | undefined,
      geoEventAlternative: c.geoEventAlternative as string | undefined,
      recommendWhen: c.recommendWhen as string | undefined,
    }));
  }

  return {
    inputs,
    outputs,
    processors,
    filters,
    inputsByConnector,
    outputsByConnector,
    processorsByUri,
    filtersByType,
    parameterLabels,
    adaptersByUri,
    transportsByUri,
    velocityOnlyComponents,
  };
}

// Node-only file-loading helper is intentionally NOT re-exported here.
// Import it directly from `./matrix-loader-node.js` (or via
// `src/mapper/index.ts`) to keep this module's import graph free of
// `node:fs` for browser bundling.

/**
 * Gets a human-readable label for a parameter name
 */
export function getParameterLabel(matrix: LoadedFunctionalMatrix, paramName: string): string {
  const label = matrix.parameterLabels.get(paramName);
  if (label) return label;

  // Convert camelCase to Title Case as fallback
  return paramName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

/**
 * Looks up an input connector mapping
 */
export function lookupInputMapping(
  matrix: LoadedFunctionalMatrix,
  connector: string
): FunctionalMatrixEntry | undefined {
  return matrix.inputsByConnector.get(connector);
}

/**
 * Looks up an output connector mapping
 */
export function lookupOutputMapping(
  matrix: LoadedFunctionalMatrix,
  connector: string
): FunctionalMatrixEntry | undefined {
  return matrix.outputsByConnector.get(connector);
}

/**
 * Looks up a processor mapping by URI
 */
export function lookupProcessorMapping(
  matrix: LoadedFunctionalMatrix,
  processorUri: string
): FunctionalMatrixEntry | undefined {
  // Try exact match first
  let entry = matrix.processorsByUri.get(processorUri);
  if (entry) return entry;

  // Try without version suffix
  const baseUri = processorUri.replace(/\/\d+\.\d+\.\d+$/, '');
  entry = matrix.processorsByUri.get(baseUri);
  if (entry) return entry;

  return undefined;
}

/**
 * Looks up a filter mapping
 */
export function lookupFilterMapping(
  matrix: LoadedFunctionalMatrix,
  filterType: string
): FunctionalMatrixEntry | undefined {
  return matrix.filtersByType.get(filterType);
}
